import client from '../client';

export interface PendingOrderSummary {
  Id: number;
  CustomerId: string;
  CustomerName: string;
  CustomerOrderNo: string;
  OrderDate: string;
  ShipToName: string;
  ItemCount: number;
  Total: number;
  LastComment: { CreateDate: string; CreateBy: string; Comment: string } | null;
}

export interface PendingOrderItem {
  PendingOrderId: number;
  Ordinal: number;
  PartNumber: string;
  Quantity: number;
  UnitPrice: number;
  Description: string;
  SeatRow: string | null;
  SeatType: string | null;
  VehicleModels: string | null;
  Extras: string | null;
  ItemDiscount: number;
  ItemShipping: number;
  PartNumberValid: boolean;
}

export interface PendingOrderLog {
  Id: number;
  PendingOrderId: number;
  ActionId: number;
  Comment: string;
  CreateBy: string;
  CreateDate: string;
}

export interface PendingOrderDetail {
  Id: number;
  CustomerId: string;
  CustomerOrderNo: string;
  CustomerEmail: string;
  OrderDate: string;
  ShipToName: string;
  ShipToAddress1: string;
  ShipToAddress2: string;
  ShipToCity: string;
  ShipToState: string;
  ShipToZipCode: string;
  ShipToCountry: string;
  ShipVia: string;
  DropShip: boolean;
  Terms: string;
  Note: string | null;
  Discount: number;
  ShippingTotal: number;
  Closed: number;
  CreateBy: string;
  CreateDate: string;
  InSystem: boolean;
  Items: PendingOrderItem[];
  Log: PendingOrderLog[];
}

export const pendingOrderService = {
  getAll: async (): Promise<PendingOrderSummary[]> => {
    const res: any = await client.get('/pending-orders');
    return Array.isArray(res.data) ? res.data : [];
  },

  getById: async (id: number): Promise<PendingOrderDetail> => {
    const res: any = await client.get(`/pending-orders/${id}`);
    return res.data;
  },

  updateShipping: async (id: number, data: {
    ShipToName: string; ShipToAddress1: string; ShipToAddress2: string;
    ShipToCity: string; ShipToState: string; ShipToZipCode: string;
    ShipToCountry: string; ShipVia: string;
  }): Promise<void> => {
    await client.put(`/pending-orders/${id}/shipping`, data);
  },

  updateItemPartNumber: async (id: number, ordinal: number, partNumber: string): Promise<void> => {
    await client.put(`/pending-orders/${id}/items/${ordinal}/part-number`, { partNumber });
  },

  addLog: async (id: number, comment: string): Promise<void> => {
    await client.post(`/pending-orders/${id}/log`, { comment });
  },

  confirm: async (id: number, comment: string): Promise<void> => {
    await client.post(`/pending-orders/${id}/confirm`, { comment });
  },

  cancel: async (id: number): Promise<void> => {
    await client.post(`/pending-orders/${id}/cancel`);
  },

  addItem: async (id: number, data: { partNumber: string; quantity: number; unitPrice: number }): Promise<void> => {
    await client.post(`/pending-orders/${id}/items`, data);
  },

  deleteItem: async (id: number, ordinal: number): Promise<void> => {
    await client.delete(`/pending-orders/${id}/items/${ordinal}`);
  },

  updateItem: async (id: number, ordinal: number, data: { quantity?: number; unitPrice?: number }): Promise<void> => {
    await client.put(`/pending-orders/${id}/items/${ordinal}`, data);
  },

  getPartInfo: async (partNumber: string): Promise<{ description: string | null }> => {
    const res: any = await client.get(`/pending-orders/part-info/${encodeURIComponent(partNumber)}`, {
      validateStatus: (s) => s < 500,
    });
    return res.data ?? { description: null };
  },
};
